import { newRelic } from '~/utils/newRelic/newRelic';

import {
  type ScanEventProcessor,
  type ScanEventProcessorOptions,
} from '../types/scanEventProcessor.type';
import { createScanEvent } from './createScanEvent.util';
import { normalizeBarcodeValue } from './normalization.util';

/**
 * Turns queued raw scan events into enriched scan events and hands them to the
 * action handler, one at a time, until the queue is empty. Each event waits for its
 * own context to resolve before the next one is dequeued. Events from users without
 * full access are dropped after dequeue and never reach barcode lookup or actions.
 */
export const createScanEventProcessor = ({
  canProcessScanEvent,
  getScanContext,
  handleScanEvent,
  queue,
}: ScanEventProcessorOptions): ScanEventProcessor => {
  let isDraining = false;
  let needsDrain = false;

  const process = async () => {
    if (isDraining) {
      needsDrain = true;
      return;
    }

    isDraining = true;

    try {
      let rawScanEvent = queue.dequeue();

      while (rawScanEvent) {
        if (!canProcessScanEvent()) {
          newRelic.log('Scan event dropped: user is not on a Pro plan', {
            customAttributes: { correlationId: rawScanEvent.correlation_id },
            level: 'DEBUG',
          });
          rawScanEvent = queue.dequeue();
          continue;
        }

        try {
          // eslint-disable-next-line no-await-in-loop
          const scanContext = await getScanContext({
            barcodeValue: normalizeBarcodeValue(rawScanEvent.barcode_value),
            correlationId: rawScanEvent.correlation_id,
          });

          handleScanEvent(createScanEvent(rawScanEvent, scanContext));
        } catch (error) {
          newRelic.noticeError('Failed to process scan event', {
            correlationId: rawScanEvent.correlation_id,
            error,
          });
        }

        rawScanEvent = queue.dequeue();
      }
    } finally {
      isDraining = false;
      if (needsDrain) {
        needsDrain = false;
        await process();
      }
    }
  };

  return { process };
};
